import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import ReactGA from 'react-ga';

// This isn't really a normal React component, but it needs to be a function so it can receive a callback
export default (handleBugReportClick = () => undefined) => ([
  {
    slug: 'getting-started',
    title: 'Getting Started',
    subsections: [
      {
        heading: 'Assemble Your Team',
        content: (
          <Fragment>
            <p>
              Invite your co-workers to join the institution account to view or contribute to assessments.
            </p>
            <p>
              An Administrator can add people to the institution account by navigating to the <Link to="/account">Manage Account</Link> screen and clicking the <strong>Invite Users</strong> button. Here’s what you need to know when you invite users to join your account:
            </p>
            <img src="/img/help/invite-new-user.png" alt="Invite a New User Modal" />
            <ul>
              <li>
                You can choose whether a person is an <strong>Administrator</strong>, <strong>Editor</strong> or <strong>Viewer</strong>; this designation is useful for contributing purposes but doesn’t impact any other part of your account. You can get more details on permissions in the Manage Account section of this help center.
              </li>
              <li>
                The email address must be unique for every person on the account.
              </li>
              <li>
                An email will be sent to your teammate(s) to let them know how to sign in and set up their password once you click <strong>Send Invitation</strong>.
              </li>
              <li>
                Users invited to an account will be linked to the institution’s database of assessments.
              </li>
              <li>
                Account permissions can be changed by an Administrator once they have set up their account.
              </li>
            </ul>
          </Fragment>
        ),
      },
    ],
  }, {
    slug: 'manage-account',
    title: 'Manage Account',
    subsections: [
      {
        heading: 'Permission Levels',
        content: (
          <Fragment>
            <p>
              The online tool has four tiers of permissions for access:
            </p>
            <ul>
              <li>
                <strong>Administrators</strong> can invite additional users to the institution account, change permissions for other users and receive email notifications about activity in the tool.
              </li>
              <li>
                <strong>Editors</strong> are allowed to create new and contribute to in-progress assessments.
              </li>
              <li>
                <strong>Viewers</strong> can view assessments but cannot create new assessments, contribute to in-progress assessments, or upload PDF assessments.
              </li>
              <li>
                <strong>Deactivated</strong> users are no longer allowed access to the tool.
              </li>
            </ul>
            <p>
              Note: It is highly recommended to have at least one active Administrator associated with the institution account at all times to maintain full account management.
            </p>
          </Fragment>
        ),
      }, {
        heading: 'Notification Emails',
        content: (
          <Fragment>
            <p>
              Account users with Administrator permissions can receive notification emails about activity in the tool. You can set your preferences under <strong>Notification Emails</strong> in the Manage Account section:
            </p>
            <img src="/img/help/notifications.png" alt="Notification preferences" />
            <ul>
              <li>
                <strong>Invitation Accepted</strong> sends an email notification when an invited user creates their account.
              </li>
              <li>
                <strong>Account Permissions Change</strong> sends an email notification when any user’s account permissions are changed.
              </li>
              <li>
                <strong>Assessment Completed</strong> sends an email notification when any user on the account completes and submits an assessment.
              </li>
              <li>
                <strong>Retake Assessment Reminder</strong> sends an email reminder to retake assessments based on a customizable duration of time.
              </li>
            </ul>
          </Fragment>
        ),
      }, {
        heading: 'Change Password',
        content: (
          <Fragment>
            <ul>
              <li>
                Navigate to <Link to="/account">Manage Account</Link> from the menu or click the icon <img src="/img/help/manage-account.png" alt="Manage Account icon" /> in the top right corner of the browser window.
              </li>
              <li>
                In the <strong>Your Information</strong> section, click the edit icon <img src="/img/help/edit.png" alt="Edit icon" /> to the right of <strong>Change Password</strong>.
              </li>
              <li>
                This will open a modal for you to enter your current password and choose your new password.
              </li>
              <li>
                Click the <strong>Save Changes</strong> button.
              </li>
            </ul>
          </Fragment>
        ),
      }, {
        heading: 'Deleting User Accounts',
        content: (
          <Fragment>
            <p>
              There is no ability to delete a user account, but a user account can be deactivated by an Administrator by changing the permissions.
            </p>
            <p>
              Note: Once a user has been made an Administrator, they cannot be demoted to a lower permissions level and cannot be deactivated.
            </p>
          </Fragment>
        ),
      },
    ],
  }, {
    slug: 'dashboard',
    title: 'Dashboard',
    subsections: [
      {
        content: (
          <Fragment>
            <p>
              The dashboard is your home screen once you’ve logged into your account. On your dashboard you will find your three most recent assessments as well as quick links to <strong>Start New Assessment</strong>, <strong>Compare Assessments</strong>, and <strong>Upload Assessment</strong>.
            </p>
            <p>
              The dashboard can be accessed by the icon <img src="/img/help/dashboard-icon.png" alt="Dashboard icon" /> at the top of the navigation bar, or by clicking <strong>Home</strong> in the menu.
            </p>
            <img src="/img/help/dashboard.png" alt="Dashboard screen" />
          </Fragment>
        ),
      },
    ],
  }, {
    slug: 'assessments',
    title: 'Assessments',
    subsections: [
      {
        heading: 'Introduction and Instructions',
        content: (
          <Fragment>
            <p>
              Errors associated with injectable medications are more likely to result in death and morbidity than medications given through other routes. Risk of error is present with medication preparation and manipulation as well as administration. It is for that reason that The Joint Commission’s Medication Management standards direct that medications be dispensed in the most ready-to-administer (RTA) form as possible. Even with that goal in mind, the reality is that there are often barriers.
            </p>
            <p>
              <strong>This tracer tool has been designed to identify those barriers in order to tailor performance improvement efforts and achieve the goal of dispensing medications in the most ready-to-administer form possible.</strong>
            </p>
          </Fragment>
        ),
      }, {
        heading: 'The Role of the Tracer Methodology',
        content: (
          <Fragment>
            <p>
              Tracer methodology, created by The Joint Commission, assesses the processes, communication, and coordination of the delivery of care in health care organizations. Tracer methodology is a powerful tool that uses observations, document review, and interviews to understand the current state of practices and identify opportunities for improvement.
            </p>
            <p>
              This tracer tool assesses how injectable medications are provided at various points of care and decision-making and identifies the factors that lead to either successful provision of medications in a ready-to-administer form or that prevent such administration. <strong>This tracer tool is designed to help hospitals in assessing the challenges and barriers and prioritizing and developing corrective action plans to improve the safety of injectable medications.</strong>
            </p>
            <p>
              The tool is composed of four tracer modules that can be completed individually or together:
            </p>
            <p>
              <strong>Patient Safety and Regulatory Compliance Tracer</strong>
              <br />
              Addresses leadership philosophy, understanding of regulatory requirements, and direction for how the pharmacy provides injectable medications
            </p>
            <p>
              <strong>Pharmacy Tracer</strong>
              <br />
              Addresses medication purchasing, medication order configuration in computerized provider order entry (CPOE) systems, medication storage, medication order verification, and medication dispensing practices
            </p>
            <p>
              <strong>Nursing Unit Tracer</strong>
              <br />
              Addresses nurses’ processes, challenges, and barriers regarding injectable medications
            </p>
            <p>
              <strong>Anesthesia Tracer</strong>
              <br />
              Addresses anesthesia providers’ processes, challenges, and barriers regarding injectable medications
            </p>
          </Fragment>
        ),
      }, {
        heading: 'Who Should Participate',
        content: (
          <Fragment>
            <ul>
              <li>
                The person(s) performing the tracers should be unbiased, objective, and supportive to obtain honest feedback from staff. Because of the pharmacist’s influence in decisions relating to dispensing of injectable medications, <strong>pharmacy leadership should lead and actively participate in each tracer activity</strong>. Different clinicians and professionals may have unique perspectives with injectable medications, so nursing leadership, accreditation professionals, quality management/performance improvement professionals, safety officers, and others should also participate in the tracer activity.
              </li>
              <li>
                <strong>A scribe may be valuable</strong> in documenting tracer observations and responses that can then be analyzed to identify opportunities for improvement.
              </li>
              <li>
                To fully understand variance among practitioners, <strong>observe and interview several pharmacy staff, nurses, and anesthesia providers during the tracer activity</strong>. The tracer tool allows for one-on-one conversations as well as group discussions.
              </li>
            </ul>
          </Fragment>
        ),
      }, {
        heading: 'Where to Conduct Tracers',
        content: (
          <Fragment>
            <ul>
              <li>
                This tracer activity includes targeted questions and observations for practices in the hospital pharmacy, nursing units (for example, medical/surgical, telemetry, critical care), emergency department, operating rooms, and procedural areas supported by anesthesia providers (for example, interventional radiology, gastroenterology procedures areas, cardiac catheterization procedure areas, ambulatory surgery, obstetrical operating rooms).
              </li>
              <li>
                To thoroughly assess injectable medication practices throughout the hospital, tracers should be conducted on multiple nursing units, operating rooms, and procedural areas to help identify patterns, trends, and variations in practices with injectable medications throughout the organization.
              </li>
            </ul>
          </Fragment>
        ),
      }, {
        heading: 'Time Commitment',
        content: (
          <Fragment>
            <ul>
              <li>
                Each set of observations and questions asked of clinicians should take <strong>approximately 20 to 30 minutes</strong>. Interviews can be one-on-one discussions or group conversations.
              </li>
              <li>
                Many injectable medication practices change over time for varying reasons, including medication shortages. Therefore, <strong>use this tool periodically to reassess injectable medication processes and the impact of new barriers</strong>.
              </li>
            </ul>
          </Fragment>
        ),
      }, {
        heading: 'Starting New Assessments',
        content: (
          <Fragment>
            <p>
              The tracer tool is composed of a series of modules designed to help hospitals in assessing the challenges and barriers and prioritizing and developing corrective action plans to improve the safety of injectable medications.
            </p>
            <p>
              You can start a new assessment by clicking the <strong>Start New Assessment</strong> button on the dashboard. On the following screen, there is a description of what each module addresses.
            </p>
          </Fragment>
        ),
      }, {
        heading: 'Flagged Questions',
        content: (
          <Fragment>
            <p>
              While working on an assessment, you can click on the flag button <img src="/img/help/flag-button.png" alt="Flag icon" /> to mark a question. Flagging is the digital equivalent of dog-earing a page or circling a question on the print version for later reference.
            </p>
            <p>
              Flagging questions can be helpful to draw attention to an opportunity for improvement or a change over time. You can see how many questions you’ve flagged in a given assessment by hovering over the flag icon on the Dashboard:
            </p>
            <img src="/img/help/flagged-dashboard.png" alt="Flagged questions" />
          </Fragment>
        ),
      }, {
        heading: 'Completing and Saving Assessments',
        content: (
          <Fragment>
            <p>
              As you progress through an assessment, your responses are automatically saved each time you advance to the next question. You can exit an assessment survey at any time and leave it for yourself or a co-worker to complete at a later date. The assessment will keep its “In Progress” status until you advance to the final question and click <strong>Submit Assessment</strong>. <strong>Once an assessment has been submitted, it can no longer be edited.</strong>
            </p>
            <p>
              Note: In order to submit an assessment, it’s not necessary to fill out a response for each question. If one or more questions are not applicable to your institution, you may advance past them.
            </p>
          </Fragment>
        ),
      }, {
        heading: 'Comparing Assessments',
        content: (
          <Fragment>
            <p>
              From the <Link to="/assessments">Assessments</Link> screen, assessment modules of the same type (up to three assessments) can be selected to generate a side-by-side comparison. These reports can be downloaded as PDFs.
            </p>
          </Fragment>
        ),
      }, {
        heading: 'Uploading PDFs',
        content: (
          <Fragment>
            <p>
              Downloaded and printed tracer tool modules, once completed, can be scanned, uploaded and archived for reference. Modules completed in print cannot be directly compared to digital versions within the digital interface.
            </p>
            <p>
              To upload a PDF to the digital tool, click the <strong>Upload Assessment</strong> button on the Dashboard. You will be prompted to choose the module type for your assessment.
            </p>
          </Fragment>
        ),
      }, {
        heading: 'Deleting Assessments',
        content: (
          <Fragment>
            <p>
              There is no ability to delete assessments once they have been started. While an assessment is in progress its responses can be edited, but once it has been submitted as a finished assessment, it will be stored in your assessments catalog and can no longer be edited.
            </p>
          </Fragment>
        ),
      },
    ],
  }, {
    slug: 'action-plan',
    title: 'Action Plan',
    subsections: [
      {
        content: (
          <Fragment>
            <p>
              Once you’ve completed an assessment and have identified opportunities for improvement, you may want to create an action plan. Having a plan on paper can help clarify goals, assign responsibilities, and improve accountability.
            </p>
            <p>
              We have provided a basic What/Who/When template that will get you started. You can download the worksheet
              {' '}
              <ReactGA.OutboundLink
                to="/documents/RTAMedSafety_ActionPlanWorksheet.docx"
                eventLabel="Download Action Plan"
                download
              >
                here
              </ReactGA.OutboundLink>
              .
            </p>
          </Fragment>
        ),
      },
    ],
  }, {
    slug: 'customer-support',
    title: 'Customer Support',
    subsections: [
      {
        heading: 'Report an Issue',
        content: (
          <Fragment>
            <p>
              If in the course of using the tracer tool you run into a software bug or need other technical support, please submit a bug report by clicking the button below. If you do not select <strong>Please follow up with me via email</strong>, your report will be anonymized.
            </p>
            <button
              type="button"
              className="button"
              onClick={handleBugReportClick}
            >
              Submit a Report
            </button>
          </Fragment>
        ),
      }, {
        heading: 'Supported Browsers',
        content: (
          <Fragment>
            <p>
              You can access RTAMedSafety.com wherever you have an internet connection, via any device running a supported browser with JavaScript enabled. If you don’t see your browser below, the digital tool may be completely accessible, but official support is not available. If you think we’ve missed a browser, please send us a bug report.
            </p>
            <p>
              Google Chrome (latest version)
              <br />
              Apple Safari (version 9+)
              <br />
              Mozilla Firefox (latest version)
              <br />
              Microsoft Edge (latest version)
              <br />
              Internet Explorer (version 11+)
            </p>
          </Fragment>
        ),
      },
    ],
  },
]);



// WEBPACK FOOTER //
// ./src/components/features/dashboard/HelpPageSectionsData.js